import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, RefreshCw, Search, Bell, Settings2, UserPlus, UserMinus, FileEdit, UserCheck, ChevronRight, KeyRound, Trash2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';

/* Admin Audit Log — reskinned to the redesigned "Audit log" mockup. Page header +
   toolbar (search / who / type / time filters) over a card of expandable rows; each
   row expands to a before/after diff (After = the recorded new values; prior values
   are not captured by the backend) plus a mono actor·timestamp line. All data wiring
   preserved — reads GET /auth/admin/audit. Chrome comes from AdminLayout; only
   the audit-row styling below is page-specific. */

type AuditEntry = {
  id: string;
  actorUid?: string | null;
  actorEmail?: string | null;
  action: string;
  target?: string | null;
  targetName?: string | null;
  details?: any;
  at?: string;
  ts?: number;
};

const ACTION_META: Record<string, { label: string; icon: any; bg: string; color: string }> = {
  'user.update':       { label: 'Client updated',     icon: FileEdit,  bg: '#FFF7ED', color: '#C2410C' },
  'notification.send': { label: 'Notification sent',   icon: Bell,      bg: '#EFF6FF', color: '#1D4ED8' },
  'config.update':     { label: 'Config changed',      icon: Settings2, bg: '#EEF2FF', color: '#4338CA' },
  'admin.add':         { label: 'Admin added',         icon: UserPlus,  bg: '#ECFDF5', color: '#047857' },
  'admin.remove':      { label: 'Admin removed',       icon: UserMinus, bg: '#FEF2F2', color: '#B91C1C' },
  'admin.role':        { label: 'Admin role changed',  icon: ShieldCheck, bg: '#EEF2FF', color: '#4338CA' },
  'lead.update':       { label: 'Lead updated',        icon: UserCheck, bg: '#F0FDFA', color: '#0F766E' },
  'user.create':       { label: 'Client created',      icon: UserPlus,  bg: '#ECFDF5', color: '#047857' },
  'user.password':     { label: 'Password reset',      icon: KeyRound,  bg: '#FEFCE8', color: '#A16207' },
  'user.delete':       { label: 'Client deleted',      icon: Trash2,    bg: '#FEF2F2', color: '#B91C1C' },
};
const metaOf = (action: string) => ACTION_META[action] || { label: action, icon: FileEdit, bg: '#F3F4F6', color: '#374151' };

function relTime(ts?: number): string {
  if (!ts) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

const FIELD_LABELS: Record<string, string> = {
  paymentStatus: 'Payment', capitalReportUnlocked: 'Capital report', accountingActivated: 'Accounting module',
  consultingActivated: 'Consulting module', adminNotes: 'Internal notes', orderStatus: 'Order status',
  manualPayments: 'Manual payments', role: 'Role',
};
const humanizeKey = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').replace(/^./, c => c.toUpperCase()).trim();
function friendlyValue(k: string, v: any): string {
  if (typeof v === 'boolean') return v ? 'Enabled' : 'Disabled';
  if (k === 'paymentStatus') return v === 'paid' ? 'Paid' : 'Not paid';
  if (k === 'orderStatus') return humanizeKey(String(v));
  if (Array.isArray(v)) return `${v.length} item${v.length !== 1 ? 's' : ''}`;
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}
// The non-"values" detail fields, rendered as friendly labelled rows (no JSON).
function detailRowsFor(e: AuditEntry): [string, string][] {
  const d = e.details || {};
  const out: [string, string][] = [];
  const push = (label: string, val: any) => { if (val !== undefined && val !== null && val !== '') out.push([label, String(val)]); };
  if (d.title != null) push('Notification title', d.title);
  if (d.count != null) push('Recipients', d.count);
  if (d.emailed != null) push('Emailed a copy to', `${d.emailed} recipient${d.emailed !== 1 ? 's' : ''}`);
  if (d.status != null) push('New status', humanizeKey(String(d.status)));
  if (Array.isArray(d.keys) && d.keys.length) push('Sections updated', d.keys.map(humanizeKey).join(', '));
  if (d.email != null) push('Email', d.email);
  if (d.role != null) push('Role', humanizeKey(String(d.role)));
  return out;
}

// Friendly names for the config documents.
const CONFIG_LABELS: Record<string, string> = {
  'pricing/current': 'Accounting Pricing',
  'corporateServices/current': 'Corporate Services',
  'consultingPricing/current': 'Consulting Pricing',
  'setupPricing/current': 'Setup & Operate Pricing',
  'reportPricing/current': 'Report Pricing',
  'calendar/standard': 'Compliance Calendar',
  'assessmentContent/current': 'Assessment Content',
};
const actorName = (e: AuditEntry) => { const em = e.actorEmail || ''; return em ? (em.split('@')[0] || em) : 'An admin'; };
const initials = (email?: string | null) => {
  const s = (email || '').trim();
  if (!s) return 'A';
  const local = s.split('@')[0] || s;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};
const fmtFull = (at?: string) => at
  ? new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '';

// The changed field → new value pairs (only user.update carries these).
function changesFor(e: AuditEntry): [string, string][] {
  const d = e.details || {};
  if (e.action === 'user.update' && d.values && typeof d.values === 'object') {
    return Object.entries(d.values).map(([k, v]) => [FIELD_LABELS[k] || humanizeKey(k), friendlyValue(k, v)] as [string, string]);
  }
  return [];
}

// One-line plain-English summary of the entry (used by the search filter).
function summarize(e: AuditEntry): string {
  const d = e.details || {};
  if (e.action === 'user.update' && d.values) {
    return Object.entries(d.values)
      .map(([k, v]) => `${FIELD_LABELS[k] || humanizeKey(k)} set to ${friendlyValue(k, v)}`)
      .join(', ');
  }
  if (e.action === 'notification.send') return d.title ? `Sent “${d.title}” to ${d.count || 1} recipient${(d.count || 1) !== 1 ? 's' : ''}` : `Sent to ${d.count || 1} recipient(s)`;
  if (e.action === 'config.update') return d.keys ? `Updated ${(d.keys || []).map(humanizeKey).join(', ')}` : 'Updated configuration';
  if (e.action === 'lead.update') return d.status ? `Marked as ${humanizeKey(String(d.status))}` : '';
  if (e.action === 'admin.add') return 'Granted admin access';
  if (e.action === 'admin.remove') return 'Removed admin access';
  return '';
}

const CHEV = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")";

const CSS = `


/* Rows run to the card edge, so this one clips. */
.adminx .card{overflow:hidden}

.adminx .toolbar{display:flex;align-items:center;gap:10px;padding:14px 16px;border-bottom:1px solid #EFF1F4;flex-wrap:wrap}
.adminx .search{position:relative;flex:1;min-width:210px}
.adminx .search svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);width:15px;height:15px;color:#94A3B8}
.adminx .search input{width:100%;border:1px solid #E6E9ED;border-radius:9px;padding:9px 12px 9px 33px;background:#fff;font:inherit;font-size:13px;color:#0F172A;transition:.14s}
.adminx .search input:hover{border-color:#CBD5E1}
.adminx .search input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft)}
.adminx select.sel{border:1px solid #E6E9ED;border-radius:9px;padding:9px 30px 9px 12px;background:#fff ${CHEV} no-repeat right 10px center;font:inherit;font-size:12.5px;font-weight:500;color:#334155;cursor:pointer;transition:.14s;-webkit-appearance:none;appearance:none;max-width:200px}
.adminx select.sel:hover{border-color:#CBD5E1}
.adminx select.sel:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-soft)}

.adminx .sub{padding:10px 18px;font-size:11.5px;color:#94A3B8;border-bottom:1px solid #EFF1F4;background:#FCFCFD}

.adminx .arow{border-bottom:1px solid #F1F4F7}
.adminx .arow:last-child{border-bottom:none}
.adminx .ahead{display:grid;grid-template-columns:minmax(168px,1.3fr) minmax(190px,2fr) 152px 92px 20px;gap:14px;align-items:center;padding:13px 18px;cursor:pointer;transition:.12s;text-align:left;width:100%;background:none;border:none;font-family:inherit}
.adminx .ahead:hover{background:#F8FAFC}
.adminx .arow.open>.ahead{background:#F8FAFC}

.adminx .actor{display:flex;align-items:center;gap:10px;min-width:0}
.adminx .avatar{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,var(--brand),var(--brand-lt));color:#fff;display:grid;place-items:center;font-size:12px;font-weight:700;flex:none}
.adminx .actor .nm{min-width:0}
.adminx .actor .nm b{font-size:13px;font-weight:600;color:#0F172A;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.adminx .actor .nm span{font-size:11px;color:#94A3B8;display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

.adminx .act{font-size:13px;color:#475569;line-height:1.4}
.adminx .act b{color:#0F172A;font-weight:600}

.adminx .ah-type{min-width:0}
.adminx .tbadge{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 10px;font-size:11px;font-weight:600;white-space:nowrap;max-width:100%}
.adminx .tbadge svg{width:13px;height:13px;flex:none}
.adminx .tbadge span{overflow:hidden;text-overflow:ellipsis}

.adminx .when{font-size:12px;color:#94A3B8;font-weight:500;white-space:nowrap;text-align:right}
.adminx .chev{width:18px;height:18px;color:#CBD5E1;transition:transform .18s;justify-self:center}
.adminx .arow.open>.ahead .chev{transform:rotate(90deg);color:var(--brand)}

.adminx .adet{padding:2px 18px 18px 62px;background:#F8FAFC;border-top:1px solid #F1F4F7}
.adminx .diff{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:14px 0 12px}
.adminx .dbox{border-radius:10px;padding:12px 14px;border:1px solid transparent}
.adminx .dbox.before{background:#FEF2F2;border-color:#FECACA}
.adminx .dbox.after{background:#F0FDF4;border-color:#BBF7D0}
.adminx .dlab{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;display:block;margin-bottom:8px}
.adminx .dbox.before .dlab{color:#B91C1C}
.adminx .dbox.after .dlab{color:#15803D}
.adminx .dline{display:flex;justify-content:space-between;gap:12px;font-size:12.5px;padding:3px 0}
.adminx .dline b{font-weight:500;color:#475569}
.adminx .dbox.after .dline span{font-weight:600;color:#166534;text-align:right}
.adminx .dval.muted{font-size:12.5px;color:#B91C1C;opacity:.75;font-style:italic}
.adminx .novalue{margin:14px 0 12px;font-size:12.5px;color:#94A3B8;font-style:italic}

.adminx .drows{display:grid;gap:2px;margin-bottom:12px}
.adminx .drow{display:flex;gap:10px;font-size:12.5px;padding:3px 0}
.adminx .drow>span{color:#94A3B8;min-width:140px;flex:none}
.adminx .drow>b{font-weight:600;color:#334155}

.adminx .amono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;color:#94A3B8;border-top:1px dashed #E2E8F0;padding-top:10px;margin-top:4px;word-break:break-all}

.adminx .loading{display:flex;justify-content:center;padding:70px 0}
.adminx .loader{width:30px;height:30px;border:3px solid var(--brand-soft);border-top-color:var(--brand);border-radius:50%;animation:ax-spin .8s linear infinite}

@media(max-width:820px){
  .adminx .ahead{grid-template-columns:1fr auto 20px}
  .adminx .ah-type,.adminx .when{display:none}
  .adminx .adet{padding-left:18px}
}
`;

export default function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [filterWho, setFilterWho] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterTime, setFilterTime] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/auth/admin/audit?limit=300`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load audit log');
      setEntries(json.entries || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const actors = useMemo(
    () => Array.from(new Set(entries.map(e => e.actorEmail).filter(Boolean))) as string[],
    [entries]
  );
  const types = useMemo(
    () => Array.from(new Set(entries.map(e => e.action).filter(Boolean))) as string[],
    [entries]
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const now = Date.now();
    const winMs = filterTime === '24h' ? 864e5 : filterTime === '7d' ? 6048e5 : filterTime === '30d' ? 2592e6 : 0;
    return entries.filter(e => {
      if (filterWho && (e.actorEmail || '') !== filterWho) return false;
      if (filterType && (e.action || '') !== filterType) return false;
      if (winMs && (e.ts || 0) < now - winMs) return false;
      if (!s) return true;
      return (
        (e.actorEmail || '').toLowerCase().includes(s) ||
        (e.action || '').toLowerCase().includes(s) ||
        (e.target || '').toLowerCase().includes(s) ||
        (e.targetName || '').toLowerCase().includes(s) ||
        summarize(e).toLowerCase().includes(s)
      );
    });
  }, [entries, q, filterWho, filterType, filterTime]);

  return (
    <AdminLayout
      section="System"
      title="Audit log"
      icon={ShieldCheck}
      subtitle="Every admin change to a client's access, billing or workflow — plus notifications, config edits and admin-role changes — is recorded here. Click any entry to see the details."
      actions={
        <button className="btn" onClick={load} disabled={loading}>
          <RefreshCw className={loading ? 'spin' : ''} /> Refresh
        </button>
      }
    >
      <style>{CSS}</style>

      {error && <div className="errbar">{error}</div>}

      <div className="card">
        <div className="toolbar">
          <div className="search">
            <Search />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by admin, action, client…"
            />
          </div>
          <select className="sel" value={filterWho} onChange={e => setFilterWho(e.target.value)} aria-label="Filter by admin">
            <option value="">All admins</option>
            {actors.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="sel" value={filterType} onChange={e => setFilterType(e.target.value)} aria-label="Filter by type">
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{metaOf(t).label}</option>)}
          </select>
          <select className="sel" value={filterTime} onChange={e => setFilterTime(e.target.value)} aria-label="Filter by time">
            <option value="">Any time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>

        {!loading && !error && (
          <div className="sub">Showing {filtered.length} of {entries.length} events</div>
        )}

        {loading ? (
          <div className="loading"><div className="loader" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            {entries.length === 0 ? 'No audit events recorded yet.' : 'No events match your filters.'}
          </div>
        ) : (
          filtered.map(e => {
            const meta = metaOf(e.action);
            const Icon = meta.icon;
            const d = e.details || {};
            const tgt = e.targetName || e.target;
            const who = <b>{actorName(e)}</b>;
            const changes = changesFor(e);
            const rows = detailRowsFor(e);
            const isOpen = openId === e.id;

            let headline: React.ReactNode;
            switch (e.action) {
              case 'user.update': headline = <>{who} updated {tgt ? <b>{tgt}</b> : 'a client'}</>; break;
              case 'notification.send': headline = <>{who} sent a notification to <b>{d.count || 1} client{(d.count || 1) !== 1 ? 's' : ''}</b></>; break;
              case 'config.update': headline = <>{who} updated <b>{CONFIG_LABELS[e.target || ''] || humanizeKey(e.target || 'settings')}</b></>; break;
              case 'lead.update': headline = <>{who} moved a lead to <b>{humanizeKey(String(d.status || ''))}</b></>; break;
              case 'admin.add': headline = <>{who} granted admin access to {tgt ? <b>{tgt}</b> : 'a user'}</>; break;
              case 'admin.remove': headline = <>{who} removed admin access</>; break;
              case 'admin.role': headline = <>{who} changed an admin's role to <b>{humanizeKey(String(d.role || ''))}</b></>; break;
              case 'user.create': { const c = tgt || d.email; headline = <>{who} created a client{c ? <> <b>{c}</b></> : ''}</>; break; }
              case 'user.password': headline = <>{who} reset a client's password</>; break;
              case 'user.delete': { const c = d.email || tgt; headline = <>{who} deleted a client{c ? <> <b>{c}</b></> : ''}</>; break; }
              default: headline = <>{who} — {meta.label}</>;
            }

            return (
              <div key={e.id} className={`arow${isOpen ? ' open' : ''}`}>
                <button
                  type="button"
                  className="ahead"
                  onClick={() => setOpenId(prev => (prev === e.id ? null : e.id))}
                  aria-expanded={isOpen}
                >
                  <div className="actor">
                    <div className="avatar">{initials(e.actorEmail)}</div>
                    <div className="nm">
                      <b>{actorName(e)}</b>
                      <span>{e.actorEmail || 'unknown admin'}</span>
                    </div>
                  </div>
                  <div className="act">{headline}</div>
                  <div className="ah-type">
                    <span className="tbadge" style={{ background: meta.bg, color: meta.color }}>
                      <Icon /><span>{meta.label}</span>
                    </span>
                  </div>
                  <div className="when">{relTime(e.ts)}</div>
                  <ChevronRight className="chev" />
                </button>

                {isOpen && (
                  <div className="adet">
                    {changes.length > 0 ? (
                      <div className="diff">
                        <div className="dbox before">
                          <span className="dlab">Before</span>
                          <div className="dval muted">Prior value not recorded</div>
                        </div>
                        <div className="dbox after">
                          <span className="dlab">After</span>
                          {changes.map(([label, val]) => (
                            <div className="dline" key={label}><b>{label}</b><span>{val}</span></div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="novalue">No value change recorded</div>
                    )}

                    {rows.length > 0 && (
                      <div className="drows">
                        {rows.map(([label, val]) => (
                          <div className="drow" key={label}><span>{label}</span><b>{val}</b></div>
                        ))}
                      </div>
                    )}

                    <div className="amono">
                      {e.actorEmail || 'unknown admin'}{e.at ? ` · ${fmtFull(e.at)}` : ''}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </AdminLayout>
  );
}
